'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import api from '@/lib/api';
import { VideoJobResponse, VideoQueueStats, HealthResponse } from '@/lib/types';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmDialog';
import VideoCard from '@/components/VideoCard';
import VideoModal from '@/components/VideoModal';
import StatusBar from '@/components/StatusBar';

export default function VideosPage() {
  const [videos, setVideos] = useState<VideoJobResponse[]>([]);
  const videosRef = useRef<VideoJobResponse[]>([]);
  const [stats, setStats] = useState<VideoQueueStats | null>(null);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<VideoJobResponse | null>(null);
  const toast = useToast();
  const { confirm } = useConfirm();

  const fetchData = useCallback(async () => {
    try {
      const [healthRes, statsRes, videosRes] = await Promise.all([
        api.getHealth(),
        api.getVideoQueueStats(),
        api.listVideoJobs(undefined, 50, 0),
      ]);
      setHealth(healthRes);
      setStats(statsRes);
      setVideos(videosRes);
      videosRef.current = videosRes;
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      const hasPendingOrProcessing = videosRef.current.some(
        v => v.status === 'pending' || v.status === 'processing'
      );
      if (hasPendingOrProcessing) {
        fetchData();
      }
    }, 3000); // Video jobs take longer, poll less frequently
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleCancel = async (jobId: string, force: boolean = false) => {
    try {
      await api.cancelVideoJob(jobId, force);
      await fetchData();
      toast.success('Video job cancelled');
    } catch (error) {
      console.error('Failed to cancel job:', error);
      toast.error('Failed to cancel job: ' + (error as Error).message);
    }
  };

  const handleDelete = async (jobId: string) => {
    const confirmed = await confirm({
      title: 'Delete Video',
      message: `Delete this video job? This cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    });

    if (confirmed) {
      try {
        await api.deleteVideoJob(jobId);
        await fetchData();
        toast.success('Video deleted');
      } catch (error) {
        console.error('Failed to delete video:', error);
        toast.error('Failed to delete video: ' + (error as Error).message);
      }
    }
  };

  const activeVideos = videos.filter(v => v.status === 'pending' || v.status === 'processing');
  const completedVideos = videos.filter(v => v.status === 'completed');
  const failedVideos = videos.filter(v => v.status === 'failed');

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <StatusBar health={health} stats={stats} onRefresh={fetchData} />

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-6 py-8 space-y-10">
          {/* Header */}
          <div className="space-y-2">
            <h1 className="text-3xl font-semibold tracking-tight">Videos</h1>
            <p className="text-[var(--text-muted)]">Generated videos from your images</p>
          </div>

          {/* Queue Stats */}
          {stats && (stats.pending > 0 || stats.processing > 0) && (
            <div className="bg-[var(--bg-elevated)] rounded-lg p-4 flex items-center gap-6">
              {stats.processing > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
                  <span className="text-sm">{stats.processing} processing</span>
                </div>
              )}
              {stats.pending > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                  <span className="text-sm">{stats.pending} in queue</span>
                </div>
              )}
              <p className="text-xs text-[var(--text-muted)] ml-auto">
                Video generation typically takes 2-10 minutes
              </p>
            </div>
          )}

          {/* Active Jobs */}
          {activeVideos.length > 0 && (
            <section className="space-y-4">
              <h2 className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wider">
                In Progress
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeVideos.map((video) => (
                  <VideoCard
                    key={video.job_id}
                    video={video}
                    onCancel={handleCancel}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Failed Jobs */}
          {failedVideos.length > 0 && (
            <section className="space-y-4">
              <h2 className="text-sm font-medium text-red-400 uppercase tracking-wider">
                Failed
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {failedVideos.map((video) => (
                  <VideoCard
                    key={video.job_id}
                    video={video}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Video Gallery */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wider">
                Video Gallery
              </h2>
              {completedVideos.length > 0 && (
                <span className="text-xs text-[var(--text-muted)]">
                  {completedVideos.length} videos
                </span>
              )}
            </div>

            {completedVideos.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-[var(--bg-elevated)] flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 010 1.972l-11.54 6.347a1.125 1.125 0 01-1.667-.986V5.653z" />
                  </svg>
                </div>
                <p className="text-[var(--text-muted)]">No videos yet</p>
                <p className="text-sm text-[var(--text-muted)] mt-1">
                  Generate videos from your images
                </p>
                <Link
                  href="/"
                  className="mt-4 px-4 py-2 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-black font-medium rounded-lg transition-colors"
                >
                  Go to Images
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {completedVideos.map((video) => (
                  <VideoCard
                    key={video.job_id}
                    video={video}
                    onSelect={setSelectedVideo}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </main>

      {/* Video Modal */}
      {selectedVideo && selectedVideo.status === 'completed' && (
        <VideoModal
          video={selectedVideo}
          videos={completedVideos}
          onClose={() => setSelectedVideo(null)}
          onNavigate={setSelectedVideo}
        />
      )}
    </div>
  );
}
